import { forwardRef, useRef, useState, useId, type ButtonHTMLAttributes, type ReactNode } from "react";
import { MessageCircle } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useLocation } from "wouter";
import { useSupport, type UseSupportOpenOptions } from "@/hooks/useSupport";
import { cn } from "@/lib/utils";

export type SupportButtonVariant =
  | "primary"
  | "secondary"
  | "ghost"
  | "link"
  | "muted";

export type SupportButtonSize = "sm" | "md" | "lg";

export interface SupportButtonProps
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, "onClick" | "children">,
    UseSupportOpenOptions {
  variant?: SupportButtonVariant;
  size?: SupportButtonSize;
  showIcon?: boolean;
  iconOnly?: boolean;
  label?: ReactNode;
  children?: ReactNode;
  /**
   * Task #1236 — controls where a click lands.
   *  - "auto"  (default): if the button has no chat-specific context
   *    (`prefill`/`context`/`subject`/`emailOverride`), navigate to the
   *    Help Centre at `/help` so renters self-serve before opening chat.
   *    With chat-specific context, open Intercom directly (preserves
   *    every existing in-flow contact CTA: booking issues, payout
   *    questions, etc.).
   *  - "help": always navigate to `/help`.
   *  - "chat": always open Intercom (legacy behaviour).
   */
  mode?: "auto" | "help" | "chat";
}

const VARIANT_CLASSES: Record<SupportButtonVariant, string> = {
  primary:
    "bg-primary text-primary-foreground shadow-sm hover:bg-primary/90 focus-visible:ring-2 focus-visible:ring-primary/40",
  secondary:
    "border border-border bg-background text-foreground hover:bg-muted focus-visible:ring-2 focus-visible:ring-ring/40",
  ghost:
    "text-foreground hover:bg-muted focus-visible:ring-2 focus-visible:ring-ring/40",
  link:
    "text-primary underline underline-offset-2 hover:no-underline focus-visible:ring-2 focus-visible:ring-ring/40 rounded-sm",
  muted:
    "text-muted-foreground hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring/40 rounded-sm",
};

const SIZE_CLASSES: Record<SupportButtonSize, string> = {
  sm: "px-4 py-1.5 text-xs",
  md: "px-6 py-2.5 text-sm",
  lg: "px-8 py-3 text-base",
};

const LINK_SIZE_CLASSES: Record<SupportButtonSize, string> = {
  sm: "text-xs",
  md: "text-sm",
  lg: "text-base",
};

/**
 * Site-wide support call-to-action. Opens the Intercom Messenger (with
 * optional prefilled message + Fin context metadata), and shows retry/email
 * alternatives when the widget is blocked or unavailable. All support buttons
 * across the app should use this component for consistent UX + telemetry.
 */
export const SupportButton = forwardRef<HTMLButtonElement, SupportButtonProps>(
  function SupportButton(
    {
      variant = "secondary",
      size = "md",
      showIcon = true,
      iconOnly = false,
      label,
      children,
      prefill,
      context,
      subject,
      eventName,
      emailOverride,
      mode = "auto",
      className,
      type = "button",
      disabled,
      visibleFallback: _visibleFallback,
      "aria-label": ariaLabel,
      ...rest
    },
    ref,
  ) {
    const restProps = rest as Record<string, unknown>;
    const testId = (restProps["data-testid"] as string | undefined) ?? "support-button";
    const { t } = useTranslation();
    const { openChat, email, emailHref } = useSupport();
    const [state, setState] = useState<"idle" | "opening" | "unavailable">("idle");
    const opening = useRef(false);
    const statusId = useId();
    const [, navigate] = useLocation();
    const text = children ?? label ?? t("common.contact_support");

    const hasChatContext =
      !!prefill || !!context || !!subject || !!emailOverride;
    const resolvedMode: "help" | "chat" =
      mode === "auto" ? (hasChatContext ? "chat" : "help") : mode;
    const onClick = () => {
      if (resolvedMode === "help") {
        // Navigate only — the destination /help page emits its own
        // page_view, and discrete help events live in the new taxonomy
        // (`help_search`, `help_article_view`, `help_helpful_vote`,
        // `help_intercom_cta_click`).
        navigate("/help");
        return;
      }
      if (opening.current) return;
      opening.current = true;
      setState("opening");
      void (async () => {
        try {
          const opened = await openChat({
            prefill, context, subject, eventName, emailOverride, visibleFallback: true,
          });
          setState(opened ? "idle" : "unavailable");
        } catch {
          setState("unavailable");
        } finally {
          opening.current = false;
        }
      })();
    };

    const isLink = variant === "link" || variant === "muted";
    const sizing = isLink ? LINK_SIZE_CLASSES[size] : SIZE_CLASSES[size];
    const radius = isLink ? "" : "rounded-full";
    const layout = isLink ? "inline-flex" : "inline-flex items-center justify-center gap-2";

    return (
      <>
      <button
        ref={ref}
        type={type}
        onClick={onClick}
        aria-label={
          iconOnly
            ? ariaLabel || (typeof text === "string" ? text : t("common.contact_support"))
            : ariaLabel
        }
        className={cn(
          "transition-colors font-semibold",
          radius,
          sizing,
          layout,
          VARIANT_CLASSES[variant],
          className,
        )}
        data-testid={testId}
        {...rest}
        disabled={disabled || state === "opening"}
        aria-busy={state === "opening" || undefined}
        aria-describedby={state !== "idle" ? statusId : rest["aria-describedby"]}
      >
        {showIcon && (
          <MessageCircle
            className={cn(
              size === "lg" ? "h-5 w-5" : size === "sm" ? "h-3.5 w-3.5" : "h-4 w-4",
              "shrink-0",
            )}
            aria-hidden
          />
        )}
        {!iconOnly && <span>{state === "opening" ? t("support.opening", { defaultValue: "Opening chat…" }) : text}</span>}
      </button>
      {state !== "idle" && (
        <div id={statusId} role="status" aria-live="polite" className="mt-2 text-sm text-muted-foreground">
          {state === "opening" ? t("support.opening", { defaultValue: "Opening chat…" }) : (
            <>
              <p>{t("support.chat_unavailable", { defaultValue: "Chat is unavailable right now. Try again or email our support team." })}</p>
              <button type="button" onClick={onClick} className="mr-3 font-medium underline underline-offset-2">
                {t("support.retry_chat", { defaultValue: "Try chat again" })}
              </button>
              <a href={emailHref({ emailOverride, subject, prefill })} className="font-medium underline underline-offset-2">
                {t("support.email_action", { defaultValue: "Email support" })}: {emailOverride || email}
              </a>
            </>
          )}
        </div>
      )}
      </>
    );
  },
);
